import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { Providers } from "@/app/providers";
import { ProtectedRoute } from "@/app/ProtectedRoute";
import { FullPageSpinner } from "@/components/layout/FullPageSpinner";
import { AppLayout } from "@/components/layout/AppLayout";
import { AdminLayout } from "@/features/admin/AdminLayout";

// Public
const RootRoute = lazy(() => import("@/features/auth/pages/RootRoute"));
const Login = lazy(() => import("@/features/auth/pages/Login"));
const Signup = lazy(() => import("@/features/auth/pages/Signup"));
const ForgotPassword = lazy(() => import("@/features/auth/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/features/auth/pages/ResetPassword"));
const NotFound = lazy(() => import("@/features/auth/pages/NotFound"));

// Authenticated
const ChangePassword = lazy(() => import("@/features/account/pages/ChangePassword"));
const Dashboard = lazy(() => import("@/features/catalog/pages/Dashboard"));
const Products = lazy(() => import("@/features/catalog/pages/Products"));
const ProductDetail = lazy(() => import("@/features/catalog/pages/ProductDetail"));
const Favorites = lazy(() => import("@/features/catalog/pages/Favorites"));
const QuickOrder = lazy(() => import("@/features/catalog/pages/QuickOrder"));
const Cart = lazy(() => import("@/features/cart/pages/Cart"));
const CheckoutSuccess = lazy(() => import("@/features/checkout/pages/CheckoutSuccess"));
const Orders = lazy(() => import("@/features/orders/pages/Orders"));
const Cashback = lazy(() => import("@/features/account/pages/Cashback"));
const Account = lazy(() => import("@/features/account/pages/Account"));
const Profile = lazy(() => import("@/features/account/pages/Profile"));
const CompanySettings = lazy(() => import("@/features/company/pages/CompanySettings"));

// Admin
const AdminDashboard = lazy(() => import("@/features/admin/pages/AdminDashboard"));
const AdminSignupRequests = lazy(() => import("@/features/admin/pages/AdminSignupRequests"));
const AdminStatistics = lazy(() => import("@/features/admin/pages/AdminStatistics"));
const AdminSalesCommission = lazy(() => import("@/features/admin/pages/AdminSalesCommission"));
const AdminCompanies = lazy(() => import("@/features/admin/pages/AdminCompanies"));
const AdminUsers = lazy(() => import("@/features/admin/pages/AdminUsers"));
const AdminPricePackages = lazy(() => import("@/features/admin/pages/AdminPricePackages"));
const AdminPrices = lazy(() => import("@/features/admin/pages/AdminPrices"));
const AdminProductAccess = lazy(() => import("@/features/admin/pages/AdminProductAccess"));
const AdminCashback = lazy(() => import("@/features/admin/pages/AdminCashback"));
const AdminCampaigns = lazy(() => import("@/features/admin/pages/AdminCampaigns"));
const AdminCollectionImages = lazy(() => import("@/features/admin/pages/AdminCollectionImages"));
const AdminOrders = lazy(() => import("@/features/admin/pages/AdminOrders"));
const AdminInvoices = lazy(() => import("@/features/admin/pages/AdminInvoices"));
const AdminMail = lazy(() => import("@/features/admin/pages/AdminMail"));
const AdminSettings = lazy(() => import("@/features/admin/pages/AdminSettings"));

export default function App() {
  return (
    <Providers>
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Authenticated app */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/skift-adgangskode" element={<ChangePassword />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/produkter" element={<Products />} />
            <Route path="/produkter/:handle" element={<ProductDetail />} />
            <Route path="/favoritter" element={<Favorites />} />
            <Route path="/hurtig" element={<QuickOrder />} />
            <Route path="/kurv" element={<Cart />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/ordrer" element={<Orders />} />
            <Route path="/cashback" element={<Cashback />} />
            <Route path="/konto" element={<Account />} />
            <Route path="/profil" element={<Profile />} />
            <Route path="/firma" element={<CompanySettings />} />
          </Route>

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireSalesperson>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="anmodninger" element={<AdminSignupRequests />} />
            <Route path="statistik" element={<AdminStatistics />} />
            <Route path="provision" element={<AdminSalesCommission />} />
            <Route path="firmaer" element={<AdminCompanies />} />
            <Route path="brugere" element={<AdminUsers />} />
            <Route path="prispakker" element={<AdminPricePackages />} />
            <Route path="priser" element={<AdminPrices />} />
            <Route path="produktadgang" element={<AdminProductAccess />} />
            <Route path="cashback" element={<AdminCashback />} />
            <Route path="kampagner" element={<AdminCampaigns />} />
            <Route path="kategori-billeder" element={<AdminCollectionImages />} />
            <Route path="ordrer" element={<AdminOrders />} />
            <Route path="fakturaer" element={<AdminInvoices />} />
            <Route path="mail" element={<AdminMail />} />
            <Route path="indstillinger" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Providers>
  );
}
